import {
  ActivityFilterKey,
  ActivityFilterModel,
  ActivityOptions,
  ActivityOrderBy,
  DateTypeFilter,
  getActivityFilter,
  Severity,
  useActivityListFilter,
  useCurrentAccount,
  useCurrentWorkspace,
  useCustomQuery,
  useSetActivityListFilter,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { keepPreviousData } from '@tanstack/react-query';
import { forwardRef, useCallback, useEffect, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { array, boolean, string, object as yupObject } from 'yup';
import { useListCount, useSlideLayoutSafe } from '../slide-layout';
import {
  FilterScreenLayout,
  FilterScreenLayoutProps,
} from './filterScreenLayout';
import { ACTIVITY_SECTIONS } from './filterSectionTypes';

export type ActivityFilterScreenHandle = {
  onReset: () => Promise<void>;
};

const formSchema = yupObject({
  orderBy: string().optional().nullable(),
  severity: array().of(string()).optional().nullable(),
  groupIds: array().of(string()).optional().nullable(),
  includeAllGroups: boolean().optional().nullable(),
  includeUngrouped: boolean().optional().nullable(),
  labelIds: array().of(string()).optional().nullable(),
  includeAllLabels: boolean().optional().nullable(),
  includeUnlabeled: boolean().optional().nullable(),
  isCombinedGroupsFilter: boolean().optional().nullable(),
  activityOption: string().optional().nullable(),
  dateType: string().optional().nullable(),
  startDate: string().optional().nullable(),
  endDate: string().optional().nullable(),
});

export type ActivityFilterScreenProps = {
  mode: 'page' | 'modal';
  onBack?: () => void;
  onDismiss?: () => void;
  isVisible?: boolean;
  filterKey?: ActivityFilterKey;
  bottomBar?: FilterScreenLayoutProps['bottomBar'];
  assetId?: string;
};

export const ActivityFilterScreen = forwardRef<
  ActivityFilterScreenHandle,
  ActivityFilterScreenProps
>(function ActivityFilterScreen(
  {
    mode,
    onBack,
    onDismiss,
    isVisible,
    filterKey = ActivityFilterKey.Activity,
    bottomBar,
    assetId,
  }: ActivityFilterScreenProps,
  ref
) {
  const { t } = useTranslation();
  const slideLayout = useSlideLayoutSafe();
  const listCount = useListCount();
  const { data: filter } = useActivityListFilter(filterKey);
  const { mutateAsync: setActivityListFilter } =
    useSetActivityListFilter(filterKey);
  const currentWorkspace = useCurrentWorkspace(true);
  const { account } = useCurrentAccount();

  const getInitialSeverity = () => {
    if (!filter?.severity || filter.severity.length === 0) return [];
    const allSeverities = Object.values(Severity);
    const hasAllSeverities =
      filter.severity.length === allSeverities.length &&
      allSeverities.every((s) => filter.severity?.includes(s));
    return hasAllSeverities ? [] : filter.severity;
  };

  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      orderBy: filter?.orderBy ?? ActivityOrderBy.MostRecentUpdated,
      severity: getInitialSeverity(),
      groupIds: filter?.groupIds ?? [],
      includeAllGroups: filter?.includeAllGroups ?? true,
      includeUngrouped: filter?.includeUngrouped ?? true,
      labelIds: filter?.labelIds ?? [],
      includeAllLabels: filter?.includeAllLabels ?? true,
      includeUnlabeled: filter?.includeUnlabeled ?? true,
      isCombinedGroupsFilter: filter?.isCombinedGroupsFilter ?? false,
      activityOption: filter?.activityOption ?? ActivityOptions.EVERYTHING,
      dateType: filter?.dateType ?? DateTypeFilter.ALL,
      startDate: filter?.startDate ?? '',
      endDate: filter?.endDate ?? '',
    },
  });

  const { control, reset, watch, setValue } = formMethods;
  const formData = watch();
  const selectedDateType = watch('dateType');

  const isGlobalAdmin = account?.isGlobalAdmin?.() || false;
  const workspaceId =
    currentWorkspace?.id ??
    (isGlobalAdmin ? undefined : account?.workspace?.id);

  const buildServerFilter = () => {
    const payload: any = { ...formData };
    if (!payload.severity || payload.severity.length === 0)
      delete payload.severity;
    if (payload.activityOption === ActivityOptions.EVERYTHING)
      delete payload.activityOption;
    const isDefaultGroups =
      (payload.includeAllGroups ?? true) &&
      (payload.includeUngrouped ?? true) &&
      (!payload.groupIds || payload.groupIds.length === 0);
    const isDefaultLabels =
      (payload.includeAllLabels ?? true) &&
      (payload.includeUnlabeled ?? true) &&
      (!payload.labelIds || payload.labelIds.length === 0);
    const hasActiveFilters =
      payload.severity ||
      payload.activityOption ||
      !isDefaultGroups ||
      !isDefaultLabels ||
      payload.dateType !== DateTypeFilter.ALL;
    if (!hasActiveFilters && !workspaceId && !isGlobalAdmin) return undefined;
    if (!hasActiveFilters && !workspaceId && isGlobalAdmin) return {};
    const activityFilterModel = new ActivityFilterModel({
      ...filter,
      ...payload,
    });
    return workspaceId
      ? getActivityFilter(activityFilterModel, workspaceId)
      : activityFilterModel.getFilter();
  };

  const serverFilterForCount = buildServerFilter();

  if (serverFilterForCount && assetId) {
    serverFilterForCount.asset = { eq: assetId };
  }

  // When the parent list provides its count via ListCountProvider (web pages
  // where filter changes auto-apply to the list), reuse it and skip this
  // duplicate count query. Mobile keeps the standalone count query because
  // its filter form doesn't auto-apply, so the prospective count differs
  // from whatever the underlying list is currently showing.
  const { data: countData } = useCustomQuery({
    cacheKey: [
      'activities',
      'filterCount',
      isGlobalAdmin ? 'global' : workspaceId || 'none',
      JSON.stringify(serverFilterForCount),
    ],
    query: `
      query FindIssuesCount($filter: FindIssuesFilter, $page: PageRequest) {
        issue {
          findIssues(filter: $filter, page: $page) {
            count
          }
        }
      }
    `,
    variables: { filter: serverFilterForCount, page: { limit: 1, offset: 0 } },
    options: {
      enabled: listCount === undefined && !!serverFilterForCount,
      placeholderData: keepPreviousData,
    },
  });
  const resultsCount =
    listCount ?? countData?.issue?.findIssues?.count ?? 0;

  useEffect(() => {
    slideLayout?.setColumnTitle(t('common.filter'));
  }, [slideLayout, t]);

  useEffect(() => {
    slideLayout?.setColumnCount(resultsCount);
  }, [slideLayout, resultsCount]);

  useEffect(() => {
    return () => {
      slideLayout?.setColumnTitle(undefined);
      slideLayout?.setColumnCount(undefined);
    };
  }, [slideLayout]);

  const onCloseAfterApply = mode === 'page' ? onBack : onDismiss;

  const onSubmit = async (data: any) => {
    const payload = { ...data };
    if (!payload.severity || payload.severity.length === 0)
      payload.severity = undefined;
    await setActivityListFilter(
      new ActivityFilterModel({ ...filter, ...payload })
    );
    onCloseAfterApply?.();
  };

  const onReset = useCallback(async () => {
    const defaultData = {
      orderBy: ActivityOrderBy.MostRecentUpdated,
      severity: [],
      activityOption: ActivityOptions.EVERYTHING,
      groupIds: [],
      includeAllGroups: true,
      includeUngrouped: true,
      labelIds: [],
      includeAllLabels: true,
      includeUnlabeled: true,
      isCombinedGroupsFilter: false,
      dateType: DateTypeFilter.ALL,
      startDate: '',
      endDate: '',
    };
    reset(defaultData);
    await setActivityListFilter(
      new ActivityFilterModel({
        ...filter,
        ...defaultData,
        severity: undefined,
      })
    );
  }, [reset, setActivityListFilter, filter]);

  useImperativeHandle(ref, () => ({ onReset }), [onReset]);

  const canReset = () =>
    (formData.severity && formData.severity.length > 0) ||
    formData.activityOption !== ActivityOptions.EVERYTHING ||
    (formData.groupIds && formData.groupIds.length > 0) ||
    !(formData.includeAllGroups ?? true) ||
    !(formData.includeUngrouped ?? true) ||
    (formData.labelIds && formData.labelIds.length > 0) ||
    !(formData.includeAllLabels ?? true) ||
    !(formData.includeUnlabeled ?? true) ||
    formData.dateType !== DateTypeFilter.ALL ||
    formData.orderBy !== ActivityOrderBy.MostRecentUpdated;

  return (
    <FilterScreenLayout
      mode={mode}
      onBack={onBack}
      onDismiss={onDismiss}
      isVisible={isVisible}
      formMethods={formMethods}
      resultsCount={resultsCount}
      viewResultsTranslationKey="activity.filter.viewResults"
      onReset={onReset}
      canReset={canReset}
      onSubmit={onSubmit}
      variant="activity"
      sections={ACTIVITY_SECTIONS}
      control={control}
      setValue={setValue}
      selectedDateType={selectedDateType ?? ''}
      workspaceId={workspaceId}
      isGlobalAdmin={isGlobalAdmin}
      bottomBar={bottomBar}
    />
  );
});
